/**
 * Legend definitions for all Atlas layers.
 * Separated from components so rebranding/updating colors only touches this file.
 */

export type LegendType = 'gradient' | 'discrete' | 'lines' | 'fills';

export interface LegendClass {
  color: string;
  label: string;
  width?: number; // for line legends
}

export interface LegendDef {
  type: LegendType;
  label: string;
  unit?: string;
  // For gradient legends — CSS gradient stops (left to right)
  gradient?: string;
  // For discrete/lines/fills legends
  classes?: LegendClass[];
}

/**
 * spectral_r gradient (blue → green → yellow → orange → red)
 * Matches TiTiler spectral_r colormap used for MDT
 */
const SPECTRAL_R = 'linear-gradient(to right, #5e4fa2, #3288bd, #66c2a5, #abdda4, #e6f598, #fee08b, #fdae61, #f46d43, #d53e4f, #9e0142)';

/**
 * Blues gradient (white → navy)
 * Matches TiTiler blues colormap used for Flow Accumulation
 */
const BLUES = 'linear-gradient(to right, #f7fbff, #deebf7, #c6dbef, #9ecae1, #6baed6, #4292c6, #2171b5, #084594)';

export const LAYER_LEGENDS: Record<string, LegendDef> = {
  // --- Terreno Base ---
  mdt: {
    type: 'gradient',
    label: 'MDT',
    unit: 'm',
    gradient: SPECTRAL_R,
  },

  // --- Morfologia ---
  slope: {
    type: 'discrete',
    label: 'Declives',
    classes: [
      { color: '#447B2B', label: '<3°' },
      { color: '#669D1D', label: '3-5°' },
      { color: '#9ED567', label: '5-9°' },
      { color: '#E8A909', label: '9-14°' },
      { color: '#EA531F', label: '14-17°' },
      { color: '#CC2708', label: '17-27°' },
      { color: '#8F1111', label: '>27°' },
    ],
  },
  aspect: {
    type: 'discrete',
    label: 'Exposição Solar',
    classes: [
      { color: '#4142A5', label: 'N' },
      { color: '#4195FF', label: 'NE' },
      { color: '#F6EF0E', label: 'E' },
      { color: '#F5B613', label: 'SE' },
      { color: '#D2240A', label: 'S' },
      { color: '#B48BB4', label: 'SW' },
      { color: '#8026A1', label: 'W' },
      { color: '#2096BE', label: 'NW' },
    ],
  },
  geomorphons: {
    type: 'discrete',
    label: 'Geomorphons',
    classes: [
      { color: '#717171', label: 'Flat' },
      { color: '#53050E', label: 'Summit' },
      { color: '#BA2231', label: 'Ridge' },
      { color: '#D45F20', label: 'Shoulder' },
      { color: '#E5CC5B', label: 'Spur' },
      { color: '#E9E998', label: 'Slope' },
      { color: '#A6BA62', label: 'Hollow' },
      { color: '#115A15', label: 'Footslope' },
      { color: '#698195', label: 'Valley' },
      { color: '#000000', label: 'Pit' },
    ],
  },
  windExposition: {
    type: 'discrete',
    label: 'Exp. Vento',
    classes: [
      { color: '#2B83BA', label: 'Abrigado' },
      { color: '#ABDDA4', label: 'Parc. Abr.' },
      { color: '#FFFFBF', label: 'Neutro' },
      { color: '#FDAE61', label: 'Parc. Exp.' },
      { color: '#D7191C', label: 'Exposto' },
    ],
  },

  // --- Erosão ---
  erosionRisk: {
    type: 'discrete',
    label: 'Erosão',
    classes: [
      { color: '#1A9641', label: 'Muito Baixo' },
      { color: '#A6D96A', label: 'Baixo' },
      { color: '#FFFFBF', label: 'Moderado' },
      { color: '#FDAE61', label: 'Alto' },
      { color: '#D7191C', label: 'Muito Alto' },
    ],
  },

  // --- Hidrologia ---
  flowAcc: {
    type: 'gradient',
    label: 'Acumulação',
    unit: 'log₁₀',
    gradient: BLUES,
  },
  streams: {
    type: 'lines',
    label: 'Linhas de Água',
    classes: [
      { color: '#74A9CF', width: 1, label: '1' },
      { color: '#5390B4', width: 1.5, label: '2' },
      { color: '#41718E', width: 2, label: '3' },
      { color: '#1F539F', width: 2.5, label: '4' },
      { color: '#1F539F', width: 3, label: '5' },
      { color: '#08306B', width: 4, label: '6' },
    ],
  },
  ridges: {
    type: 'lines',
    label: 'Festos',
    classes: [
      { color: '#C43C39', width: 1, label: '1' },
      { color: '#C43C39', width: 1.5, label: '2' },
      { color: '#BA3214', width: 2, label: '3' },
      { color: '#BA3214', width: 2.5, label: '4' },
      { color: '#BA3214', width: 3, label: '5' },
      { color: '#BA1419', width: 4, label: '6' },
    ],
  },
  basins: {
    type: 'fills',
    label: 'Bacias',
    classes: [
      { color: '#E8A4C8', label: 'A' },
      { color: '#7ECFC0', label: 'B' },
      { color: '#B5D46A', label: 'C' },
      { color: '#9B8EC4', label: 'D' },
    ],
  },
  floodZones: {
    type: 'fills',
    label: 'A. Inundáveis',
    classes: [
      { color: '#D73027', label: 'Elevado' },
      { color: '#FC8D59', label: 'Moderado' },
      { color: '#FEE08B', label: 'Baixo' },
    ],
  },

  // --- Contexto OSM ---
  landuse: {
    type: 'fills',
    label: 'Uso do Solo',
    classes: [
      { color: '#5A8A42', label: 'Floresta' },
      { color: '#D4C878', label: 'Agrícola' },
      { color: '#A0A0A0', label: 'Urbano' },
    ],
  },
};
